import React, { useCallback, useContext, useEffect, useState } from 'react';

import { Skeleton } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { BreadcrumbItemProps } from '../../../shared/components/Breadcrumbs/BreadcrumbItem/BreadcrumbItem';
import CardNE from '../../../shared/components/CardNE/CardNE';
import CardStatusNE from '../../../shared/components/CardStatusNE/CardStatusNE';
import CustomEmptyPage from '../../../shared/components/CustomEmptyPage/CustomEmptyPage';
import StyledButtonNE from '../../../shared/components/StyledButtonNE/StyledButtonNE';
import TextField from '../../../shared/components/TextField/TextField';
import SecondaryHero from '../../../shared/components/v3/Heros/SecondaryHero/SecondaryHero';
import { REWARDS_POINTS_ROUTE, LOAD_BALANCE_ROUTE } from '../../../shared/constants/auth';
import { ONLY_NUMBERS_REGEX } from '../../../shared/constants/constants';
import {
  CHANGE_COLLABORATORS_LIST_POINTS,
  CLICK_ACTION,
  LOAD_BALANCE_ALERT,
  SECTION_REWARDS_SEND_POINTS,
  SEND_REQUEST
} from '../../../shared/constants/tagManager';
import { CompanyContext } from '../../../shared/context/company/companyContext';
import { RewardsContext } from '../../../shared/context/rewards/rewardsContext';
import { ISendPointsData } from '../../../shared/interfaces/rewards';
import { ITableState } from '../../../shared/interfaces/tables';
import { Rewards } from '../../../shared/services/api/rewards/rewards';
import tagManagerService from '../../../shared/services/gtm/tagManagerService';
import { TRANSLATION_POINTS, TRANSLATION_POINTS_CONFIG } from '../../../shared/translations/rewards';
import { TRANSLATIONS_SETTINGS } from '../../../shared/translations/settings';
import { RewardsBalancePaymentMethods } from '../../../shared/types/rewards';
import { toLocaleNumberFormat } from '../../../shared/utils/utils';
import RewardsPointsEmailConfig from '../RewardsPointsEmailConfig/RewardsPointsEmailConfig';
import PointsConfigError from './sections/Error/PointsConfigError';
import PointsConfigSuccess from './sections/Success/PointsConfigSuccess';

import '../Rewards.scss';

const RewardsPointsConfig = () => {
  const [points, setPoints] = useState<string>('');
  const [isValidPoints, setIsValidPoints] = useState<boolean | null>(null);
  const [enoughBalance, setEnoughBalance] = useState<boolean | null>(null);
  const [successfulPointsSent, setSuccessfulPointsSent] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(false);
  const [tableSelection, setTableSelection] = useState<ITableState | undefined>(undefined);
  const [message, setMessage] = useState<string>('');
  const [category, setCategory] = useState<string>('default');

  const { company } = useContext(CompanyContext);
  const {
    balance,
    currency,
    internalProducts,
    loading: rewardsLoading,
    error: rewardsError,
    countryCode,
    selectedCollaborators,
    changeSelectedCollaborators,
    tableState,
    setTableState,
    updateBalance
  } = useContext(RewardsContext);

  const { t } = useTranslation();
  const navigate = useNavigate();

  const pointsConfigBreadcrumb: BreadcrumbItemProps[] = [
    { label: t(TRANSLATION_POINTS.header.breadcrumb.rewards) },
    { label: t(TRANSLATION_POINTS.header.breadcrumb.sendPoints) }
  ];

  const handleOnChangePoints = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    const isValid = checkIfPointsIsValid(value);
    if (isValid) {
      value = limitMaxPoints(value);
      setEnoughBalance(checkIfBalanceIsEnough(value));
    } else {
      setEnoughBalance(null);
    }
    setIsValidPoints(isValid);
    setPoints(value);
  };

  const handleOnChangeMessage = (message: string) => {
    setMessage(message);
  };

  const handleOnChangeCategory = (category: string) => {
    setCategory(category);
  };

  const limitMaxPoints = (value: string) => {
    const maxFigures = balance.toString().length + 3;
    return value.length > maxFigures ? value.slice(0, maxFigures) : value;
  };

  const checkIfPointsIsValid = (points: string) => {
    if (points === '') {
      return null;
    }
    if (!RegExp(ONLY_NUMBERS_REGEX).exec(points)) {
      return false;
    }
    return Number(points) > 0;
  };

  const checkIfBalanceIsEnough = (points: string) => {
    const totalPoints = Number(points) * selectedCollaborators.length;
    return totalPoints <= balance;
  };

  const getMissingBalance = () => {
    if (points === undefined) {
      return 0;
    }
    const totalPoints = Number(points) * selectedCollaborators.length;
    return toLocaleNumberFormat(totalPoints - balance);
  };

  const anyCollaboratorNotActive = useCallback(() => {
    return selectedCollaborators.some((collaborator) => collaborator.status !== 'active');
  }, [selectedCollaborators]);

  const sendPoints = async () => {
    if (!readyToSendPoints) {
      return;
    }

    const pointsProduct = internalProducts.find((product) => product.type === 'points');
    if (!pointsProduct) {
      return;
    }

    try {
      setLoading(true);

      const sendPointsData: ISendPointsData = {
        countryCode: countryCode,

        message: message,
        category: category,
        orderAmount: points !== '' ? Number(points) * selectedCollaborators.length : 0,
        paymentDetail: {
          paymentMethod: RewardsBalancePaymentMethods[RewardsBalancePaymentMethods.balance],
          currency: currency
        },
        productId: pointsProduct.id,
        productName: pointsProduct.name,
        orders: selectedCollaborators.map((collaborator) => ({
          members: [{ email: collaborator.email, name: collaborator.fullName || '' }],
          points: points
        }))
      };

      await Rewards.sendPoints(company.id, sendPointsData);
      tagManagerService.pushEvent(CLICK_ACTION, SEND_REQUEST, {
        ...SECTION_REWARDS_SEND_POINTS,
        category: category,
        has_personalized_message: !!message.length
      });

      setSuccessfulPointsSent(true);
      changeSelectedCollaborators([]);
      updateBalance(balance - sendPointsData.orderAmount);
    } catch (error) {
      setSuccessfulPointsSent(false);
    } finally {
      setLoading(false);
    }
  };

  const restartPointsConfig = () => {
    setPoints('');
    setEnoughBalance(null);
    setSuccessfulPointsSent(null);
  };

  const readyToSendPoints = () => {
    if (!isValidPoints) {
      return false;
    }
    if (!enoughBalance) {
      return false;
    }
    if (selectedCollaborators.length === 0) {
      return false;
    }
    return true;
  };

  const goToCollaboratorsList = () => {
    setTableState(tableSelection);
    tagManagerService.pushEvent(CLICK_ACTION, CHANGE_COLLABORATORS_LIST_POINTS);
    navigate(REWARDS_POINTS_ROUTE);
  };

  const goToLoadBalance = () => {
    tagManagerService.pushEvent(CLICK_ACTION, LOAD_BALANCE_ALERT);
    navigate(LOAD_BALANCE_ROUTE);
  };

  useEffect(() => {
    if (!rewardsLoading) {
      if (tableState) {
        setTableSelection(tableState);
      }
      setTableState(undefined);
    }
  }, [tableState, rewardsLoading]);

  if (successfulPointsSent) {
    return <PointsConfigSuccess breadcrumbs={pointsConfigBreadcrumb} />;
  }
  if (successfulPointsSent === false) {
    return <PointsConfigError breadcrumbs={pointsConfigBreadcrumb} retry={sendPoints} cancel={restartPointsConfig} />;
  }

  return (
    <div className="rewards-detail-container" data-experience="new">
      <SecondaryHero
        title={t(TRANSLATION_POINTS.header.heroTitle)}
        description={t(TRANSLATION_POINTS.header.heroDescription)}
        color="hero-rewards"
        backAction={goToCollaboratorsList}
      />
      <div className="rewards-detail-content">
        {rewardsError ? (
          <CustomEmptyPage
            title={t(TRANSLATIONS_SETTINGS.error.title)}
            subtitle={t(TRANSLATIONS_SETTINGS.error.subtitle)}
            newExperience={true}
          />
        ) : (
          <>
            <CardNE className="justify-between gap-4">
              <div className="flex flex-row w-full justify-between items-center">
                <div className="flex flex-column gap-2">
                  <h3 className="ne-font-body-lg font-bold m-0">{t(TRANSLATION_POINTS_CONFIG.collaborators.title)}</h3>
                  {rewardsLoading ? (
                    <Skeleton variant="text" width={400} height={30} />
                  ) : (
                    <p className="ne-font-body-sm m-0">
                      {selectedCollaborators.length > 1
                        ? t(TRANSLATION_POINTS_CONFIG.collaborators.textPlural, {
                            collaborators: selectedCollaborators.length
                          })
                        : t(TRANSLATION_POINTS_CONFIG.collaborators.text, {
                            collaborators: selectedCollaborators.length
                          })}
                    </p>
                  )}
                </div>
                <StyledButtonNE buttonType="secondary" buttonSize="sm" classes="w-auto" action={goToCollaboratorsList}>
                  {t(TRANSLATION_POINTS_CONFIG.collaborators.button)}
                </StyledButtonNE>
              </div>
              {anyCollaboratorNotActive() && (
                <CardStatusNE
                  type="alert"
                  message={
                    <Trans i18nKey={TRANSLATION_POINTS_CONFIG.collaborators.statusCard} components={{ 1: <br /> }} />
                  }
                />
              )}
            </CardNE>

            <CardNE className="justify-between gap-6">
              <h3 className="ne-font-body-lg font-bold m-0">{t(TRANSLATION_POINTS_CONFIG.points.title)}</h3>
              <TextField
                error={isValidPoints === false}
                value={points}
                onChange={handleOnChangePoints}
                data-testid="domain-input"
                name="es"
                type="number"
                label={t(TRANSLATION_POINTS_CONFIG.points.inputLabel)}
                id="pointsInput"
              />
              {points !== '' && Number(points) > 0 && (
                <div className="flex flex-row justify-between w-full">
                  <div className="flex flex-column">
                    <h4 className="ne-font-body-sm font-bold m-0 py-2">
                      {t(TRANSLATION_POINTS_CONFIG.points.table.header.collaborators)}
                    </h4>
                    <p className="ne-font-body-sm m-0 py-2">
                      {selectedCollaborators.length > 1
                        ? t(TRANSLATION_POINTS_CONFIG.points.table.values.collaboratorsPlural, {
                            collaborators: selectedCollaborators.length
                          })
                        : t(TRANSLATION_POINTS_CONFIG.points.table.values.collaborators, {
                            collaborators: selectedCollaborators.length
                          })}
                    </p>
                  </div>
                  <div className="flex flex-column">
                    <h4 className="ne-font-body-sm font-bold m-0 py-2">
                      {t(TRANSLATION_POINTS_CONFIG.points.table.header.points)}
                    </h4>
                    <p className="ne-font-body-sm m-0 py-2">
                      {Number(points) > 1
                        ? t(TRANSLATION_POINTS_CONFIG.points.table.values.pointsPlural, {
                            points: toLocaleNumberFormat(Number(points))
                          })
                        : t(TRANSLATION_POINTS_CONFIG.points.table.values.points, {
                            points: toLocaleNumberFormat(Number(points))
                          })}
                    </p>
                  </div>
                  <div className="flex flex-column">
                    <h4 className="ne-font-body-sm font-bold m-0 py-2">
                      {t(TRANSLATION_POINTS_CONFIG.points.table.header.total)}
                    </h4>
                    <p className="ne-font-body-sm m-0 py-2">
                      {Number(points) * selectedCollaborators.length > 1
                        ? t(TRANSLATION_POINTS_CONFIG.points.table.values.pointsPlural, {
                            points: toLocaleNumberFormat(Number(points) * selectedCollaborators.length)
                          })
                        : t(TRANSLATION_POINTS_CONFIG.points.table.values.points, {
                            points: toLocaleNumberFormat(Number(points) * selectedCollaborators.length)
                          })}
                    </p>
                  </div>
                </div>
              )}
              {enoughBalance === false && (
                <CardStatusNE
                  type="error"
                  message={t(TRANSLATION_POINTS_CONFIG.points.statusCard, { balance: getMissingBalance() })}
                  button={{
                    buttonType: 'tertiary',
                    buttonSize: 'md',
                    action: goToLoadBalance
                  }}
                  buttonText={t(TRANSLATION_POINTS_CONFIG.points.statusCardButton)}
                />
              )}
            </CardNE>
            <div className="emails-config">
              <RewardsPointsEmailConfig
                onChangeMessage={handleOnChangeMessage}
                onChangeCategory={handleOnChangeCategory}
                message={message}
                category={category}
              />
            </div>
            <div className="flex justify-end">
              <StyledButtonNE
                buttonType="primary"
                buttonSize="lg"
                classes="w-auto"
                action={sendPoints}
                loading={loading}
                disabled={!readyToSendPoints()}
              >
                {t(TRANSLATION_POINTS_CONFIG.button)}
              </StyledButtonNE>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RewardsPointsConfig;
